'use client';
import React, { useState } from 'react';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-2xl ${className || ''}`}
      style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}
    >
      {children}
    </section>
  );
}

function Chip({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="px-3 py-1 rounded-full hover:bg-white/25 text-sm transition"
      style={{ background: 'rgba(255,255,255,.14)' }}
    >
      {children}
    </a>
  );
}

export default function AccessibilityPage() {
  const [date] = useState(() => new Date().toLocaleDateString('ru-RU'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    url: '',
    description: '',
  });
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const downloadTxt = React.useCallback(() => {
    const text = `INTERIM — Декларация доступности
Стандарт: WCAG 2.2 AA
Статус: частичное соответствие
Обновлено: ${new Date().toLocaleDateString('ru-RU')}

Мы обеспечиваем: контраст 4.5:1, управление клавиатурой, корректные ARIA-роли, alt-тексты, субтитры и транскрипты. 
Связаться: info@interimjob.ru`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'INTERIM_Accessibility_Statement.txt';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }, []);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formStatus) {
      setFormStatus(null);
    }
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked && formStatus?.includes('согласитесь с обработкой')) {
      setFormStatus(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isConsentChecked) {
      setFormStatus('❌ Пожалуйста, согласитесь с обработкой персональных данных.');
      return;
    }

    setSubmitting(true);
    setFormStatus('Отправка...');

    try {
      const leadData = {
        ...formData,
        formName: 'Доступность'
      };

      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/feedbacks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: leadData }),
      });

      if (response.ok) {
        setFormStatus('✅ Спасибо! Сообщение отправлено.');
        setFormData({ name: '', email: '', url: '', description: '' });
        setIsConsentChecked(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setFormStatus(`❌ Ошибка: ${errorData.error?.message || 'Неизвестная ошибка'}`);
      }
    } catch (error) {
      setFormStatus('❌ Не удалось подключиться к серверу. Проверьте ваше соединение.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:bg-yellow-300 focus:text-blue-900 focus:px-3 focus:py-2 focus:rounded-lg z-50 transition"
      >
        Перейти к содержимому
      </a>

      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm" aria-label="Хлебные крошки">
            <Chip href="/">Главная</Chip>
            <Chip href="#main" aria-current="page">Доступность</Chip>
          </nav>

          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Доступность INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Мы стремимся сделать наш сайт и продукты доступными для всех пользователей, включая людей с ограничениями по зрению, слуху, моторике и когнитивным особенностям.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <button
              onClick={() => window.print()}
              className="px-4 py-3 rounded-xl font-semibold transition hover:opacity-80"
              style={{ background: 'rgba(255,255,255,.1)' }}
            >
              Печать/Сохранить в PDF
            </button>
            <button
              onClick={downloadTxt}
              className="px-4 py-3 rounded-xl font-semibold transition hover:opacity-90"
              style={{ background: '#FFC300', color: '#102a43' }}
            >
              Скачать декларацию (.txt)
            </button>
          </div>
        </div>
      </header>

      <main
        id="main"
        tabIndex={-1}
        className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16 scroll-mt-32"
      >
        <Card className="p-6">
          <h2 className="text-3xl md:text-4xl font-bold">Декларация доступности</h2>
          <p className="mt-3 text-base text-gray-200">
            INTERIM создаёт интерфейсы, контент и процессы, доступные для всех пользователей, включая людей с ограничениями по зрению и моторике. Мы соблюдаем российский стандарт доступности ГОСТ Р ИСО/МЭК 301 549. Используем удобный дизайн с высоким контрастом текста, элементы, управляемые с клавиатуры, корректные ARIA-метки и альтернативный контент для медиа.
          </p>
          <div className="mt-3 text-xs text-gray-300">
            Статус соответствия: <span className="px-2.5 py-1 rounded bg-white/15">Частичное соответствие стандарту ГОСТ Р ИСО/МЭК 301 549</span> •
            Обновлено: <span className="px-2.5 py-1 rounded bg-white/15">{date}</span>
          </div>
        </Card>

        <section aria-label="Ключевые области">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Что мы уже сделали</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              [
                'Навигация с клавиатуры',
                'Логичный таб-порядок и видимый фокус, Ссылка «Перейти к содержимому», Меню и модальные окна управляются клавиатурой'
              ],
              ['Контраст и цвета', 'Текст ≥ 4.5:1, крупный текст ≥ 3:1, элементы управления ≥ 3:1.'],
              ['Альтернативы медиа', 'Alt-тексты для изображений; субтитры/транскрипты для видео и подкастов.'],
              ['Формы', 'Подписи, описания ошибок, ассоциированные label, корректные роли/статусы.'],
              ['Совместимость', 'Тесты в NVDA/JAWS/VoiceOver, масштаб до 200%, поддержка без мыши.'],
              ['Движение', 'Учёт предпочтения пользователя к сокращённой анимации и отсутствие мерцания, способного вызвать дискомфорт.'],
            ].map(([title, desc]) => {
              const isFirst = title === 'Навигация с клавиатуры';
              return (
                <Card key={title} className="p-4">
                  <h3 className="font-semibold">{title}</h3>
                  {isFirst ? (
                    <ul className="list-disc list-inside mt-2 text-base text-gray-200">
                      {desc.split(',').map((item, i) => (
                        <li key={i}>{item.trim()}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-base text-gray-200">{desc}</p>
                  )}
                </Card>
              );
            })}
          </div>
        </section>

        <section aria-label="Горячие клавиши">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Навигация и сочетания клавиш</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm rounded-xl" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
              <thead className="text-left border-b border-white/10">
                <tr><th className="p-3">Действие</th><th className="p-3">Windows/Linux</th><th className="p-3">macOS</th></tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/5"><td className="p-3">Переход по интерактивным элементам</td><td className="p-3">Tab / Shift+Tab</td><td className="p-3">Tab / Shift+Tab</td></tr>
                <tr className="border-b border-white/5"><td className="p-3">Открыть/активировать</td><td className="p-3">Enter / Space</td><td className="p-3">Return / Space</td></tr>
                <tr className="border-b border-white/5"><td className="p-3">Закрыть модальное меню</td><td className="p-3">Esc</td><td className="p-3">Esc</td></tr>
                <tr><td className="p-3">Перейти к основному контенту</td><td className="p-3">Alt+Shift+S</td><td className="p-3">Ctrl+Option+S</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <Card className="p-6">
          <h2 className="text-2xl md:text-3xl font-bold">Известные ограничения</h2>
          <ul className="list-disc ml-6 mt-2 text-base text-gray-200 space-y-1">
            <li>Некоторые сторонние виджеты (картографические, видео) могут быть не полностью совместимы с российским стандартом ГОСТ Р ИСО/МЭК 301 549.</li>
            <li>Редкие страницы с историческим контентом могут иметь пониженный контраст изображений.</li>
            <li>Файлы PDF старых версий не прошли полноту тегирования для скринридеров.</li>
          </ul>
          <p className="text-base text-gray-200 mt-3"><b>Обходные пути:</b> альтернативные ссылки, текстовые версии, предоставление исходных данных по запросу.</p>
        </Card>

        <section aria-label="План улучшений">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">План улучшений</h2>
          <ol className="list-decimal ml-6 text-base text-gray-200 space-y-1">
            <li>Аудит доступности по стандарту ГОСТ Р ИСО/МЭК 301 549 с автоматическими (axe/Pa11y) и ручными проверками — ежеквартально.</li>
            <li>Полное тегирование PDF и добавление текстовых альтернатив — 4 квартал 2025 года.</li>
            <li>Субтитры для всех новых видео и расшифровки подкастов — постоянный процесс.</li>
            <li>Виджеты с картами — добавить ARIA-метки и управление с клавиатуры — 1 квартал 2026 года.</li>
          </ol>
        </section>

        <Card className="p-6">
          <h2 className="text-2xl md:text-3xl font-bold">Чек-лист ручного тестирования</h2>
          <ul className="mt-2 text-base text-gray-200 grid md:grid-cols-2 gap-2 list-disc ml-6">
            <li>Навигация Tab/Shift+Tab без ловушек фокуса</li>
            <li>Фокус видимый на каждом интерактивном элементе</li>
            <li>Контраст текста/иконок соответствует 4.5:1</li>
            <li>Изображения имеют информативные alt-тексты</li>
            <li>Формы: label, описания ошибок, подсказки</li>
            <li>Масштаб 200% без потери функционала</li>
            <li>Проверка с NVDA/VoiceOver/JAWS</li>
            <li>Без мерцаний {'>'} 3 вспышек/сек</li>
          </ul>
        </Card>

        <Card className="p-6">
          <h2 className="text-2xl md:text-3xl font-bold">Обратная связь</h2>
          <p className="text-sm text-gray-200 mt-1">Если вы столкнулись с барьером доступности, опишите проблему — мы ответим в течение 3 рабочих дней.</p>
          <form className="mt-4 grid gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
            <input
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-300 transition"
              placeholder="Имя*"
              required
            />
            <input
              name="email"
              value={formData.email}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-300 transition"
              placeholder="E-mail*"
              type="email"
              required
            />
            <input
              name="url"
              value={formData.url}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-yellow-300 transition"
              placeholder="Страница/URL (если есть)"
              type="url"
            />
            <textarea
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2 focus:outline-none focus:ring-2 focus:ring-yellow-300 transition"
              rows={4}
              placeholder="Опишите проблему*"
              required
            />
            <CustomCheckbox
              name="consent"
              isChecked={isConsentChecked}
              onChange={handleConsentChange}
              link="/privacy"
              textColorClass="text-gray-400"
              linkColorClass="text-[#FF7C22]"
              className="md:col-span-2"
            />
            <button
              className="px-4 py-3 rounded-xl font-semibold md:col-span-2 transition hover:opacity-90"
              style={{ background: '#FFC300', color: '#102a43' }}
              disabled={submitting}
            >
              {submitting ? 'Отправка...' : 'Отправить'}
            </button>
            {formStatus && (
              <div className={`md:col-span-2 text-center text-sm ${formStatus.startsWith('❌') ? 'text-red-400' : 'text-green-300'}`}>
                {formStatus}
              </div>
            )}
          </form>
        </Card>
      </main>
    </div>
  );
}